import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Search, RotateCcw, Package, Barcode } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce helper
const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

const PriceCheckerPage = () => {
  const { currentOrg } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(89500);
  const inputRef = useRef(null);

  // Auto-focus on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    fetchExchangeRate();
  }, []);

  // Fetch current exchange rate
  const fetchExchangeRate = async () => {
    if (!currentOrg) return;
    try {
      const res = await axios.get(`${API}/exchange-rate?organization_id=${currentOrg.id}`);
      if (res.data?.rate) {
        setExchangeRate(res.data.rate);
      }
    } catch (err) {
      console.error('Failed to fetch exchange rate:', err);
    }
  };

  // Search for item by barcode or name
  const searchItem = useCallback(async (query, isFinal = false) => {
    if (!query || !currentOrg) {
      if (!query) setItem(null);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/price-check`, {
        params: {
          query: query.trim(),
          organization_id: currentOrg.id,
          exact: isFinal
        }
      });
      
      if (res.data && res.data.found) {
        setItem(res.data.item);
      } else {
        if (isFinal) {
          setItem(null);
        }
      }
    } catch (err) {
      console.error('Search error:', err);
      if (isFinal) setItem(null);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((q) => searchItem(q, false), 100),
    [searchItem]
  );

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Handle Enter key - final search and clear
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchItem(searchQuery, true);
      setSearchQuery('');
    }
  };

  // Reload page
  const handleReload = () => {
    setItem(null);
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Format price with currency
  const formatPrice = (price, currency = 'USD') => {
    if (!price && price !== 0) return '-';
    if (currency === 'LBP') {
      return Math.round(price).toLocaleString('en-US');
    }
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate USD from LBP
  const getUSDFromLBP = (lbpPrice) => {
    if (!lbpPrice || !exchangeRate) return 0;
    return lbpPrice / exchangeRate;
  };

  // Calculate LBP from USD
  const getLBPFromUSD = (usdPrice) => {
    if (!usdPrice) return 0;
    return Math.round(usdPrice * exchangeRate);
  };

  return (
    <div className="fixed inset-0 bg-[#020617] text-white overflow-hidden flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="flex-shrink-0 px-6 py-4 border-b border-white/10 flex items-center justify-between bg-[#0f172a]/80 backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Package className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-wide text-white uppercase">
              Pharmacy Mar Youhanna
            </h1>
            <p className="text-sm text-emerald-400 font-medium">Price Checker</p>
          </div>
        </div>
        <button
          onClick={handleReload}
          className="p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
          title="Reset"
        >
          <RotateCcw className="w-5 h-5 text-white/70" />
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-start p-6 overflow-auto">
        {/* Search Input */}
        <div className="w-full max-w-4xl mb-8">
          <div 
            className="relative rounded-2xl bg-[#1e293b]/70 backdrop-blur-xl border border-white/10 transition-all duration-300 focus-within:shadow-[0_0_60px_rgba(16,185,129,0.3)] focus-within:border-emerald-500/50"
          >
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-8 h-8 text-white/40" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Scan barcode or search item..."
              className="w-full bg-transparent text-white placeholder:text-white/30 outline-none py-6 pl-20 pr-6 text-2xl font-medium tracking-wide uppercase"
              style={{ fontSize: 'clamp(1.25rem, 3vw, 2rem)' }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-testid="price-checker-input"
            />
          </div>
          {loading && (
            <div className="text-center mt-4 text-emerald-400 text-sm animate-pulse">
              Searching...
            </div>
          )}
        </div>

        {/* Results */}
        {item ? (
          <div 
            className="w-full max-w-4xl rounded-3xl bg-[#1e293b]/70 backdrop-blur-xl border border-white/10 p-10 md:p-14 animate-fadeIn"
            data-testid="price-checker-result"
          >
            {/* Item Name */}
            <h2 
              className="text-center font-bold uppercase tracking-wider text-white mb-8 break-words leading-tight"
              style={{ fontSize: 'clamp(1.5rem, 5vw, 3rem)' }}
            >
              {item.name}
            </h2>

            {/* Prices - Handle both LBP and USD items */}
            <div className="flex flex-col items-center gap-4 mb-8">
              {item.currency === 'LBP' ? (
                <>
                  {/* LBP is primary */}
                  <div className="flex items-center gap-4">
                    <span 
                      className="font-bold text-emerald-400 tabular-nums"
                      style={{ fontSize: 'clamp(3rem, 10vw, 6rem)' }}
                    >
                      {formatPrice(item.price, 'LBP')}
                    </span>
                    <span className="text-emerald-400 text-2xl font-semibold uppercase tracking-widest">
                      LBP
                    </span>
                  </div>
                  
                  {/* USD equivalent */}
                  <div className="flex items-center gap-3">
                    <span 
                      className="font-bold text-blue-400 tabular-nums"
                      style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)' }}
                    >
                      ${formatPrice(getUSDFromLBP(item.price), 'USD')}
                    </span>
                    <span className="text-blue-400 text-lg font-semibold uppercase tracking-widest">
                      USD
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* USD is primary */}
                  <div className="flex items-center gap-4">
                    <span 
                      className="font-bold text-emerald-400 tabular-nums"
                      style={{ fontSize: 'clamp(3rem, 10vw, 6rem)' }}
                    >
                      ${formatPrice(item.price, 'USD')}
                    </span>
                    <span className="text-emerald-400 text-2xl font-semibold uppercase tracking-widest">
                      USD
                    </span>
                  </div>
                  
                  {/* LBP equivalent */}
                  <div className="flex items-center gap-3">
                    <span 
                      className="font-bold text-blue-400 tabular-nums"
                      style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)' }}
                    >
                      {formatPrice(getLBPFromUSD(item.price), 'LBP')}
                    </span>
                    <span className="text-blue-400 text-lg font-semibold uppercase tracking-widest">
                      LBP
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Item Details */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-white/60">
              {item.barcode && (
                <div className="flex items-center gap-2">
                  <Barcode className="w-5 h-5" />
                  <span className="font-mono text-lg uppercase tracking-widest">
                    {item.barcode}
                  </span>
                </div>
              )}
              {item.quantity !== undefined && (
                <div className="flex items-center gap-2 text-lg">
                  <span className="text-white/40">Stock:</span>
                  <span className={`font-semibold ${item.quantity > 10 ? 'text-emerald-400' : item.quantity > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {item.quantity} {item.unit || 'units'}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Empty State */
          <div 
            className="w-full max-w-4xl flex flex-col items-center justify-center py-20 opacity-40"
            data-testid="price-checker-empty"
          >
            <Barcode className="w-32 h-32 text-white/30 mb-6" strokeWidth={1} />
            <h3 
              className="font-bold uppercase tracking-[0.3em] text-white/50"
              style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)' }}
            >
              Ready for Scan
            </h3>
            <p className="text-white/30 mt-4 text-center">
              Scan a barcode or type to search
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="flex-shrink-0 px-6 py-3 border-t border-white/10 bg-[#0f172a]/50 text-center">
        <p className="text-xs text-white/30">
          Rate: 1 USD = {exchangeRate.toLocaleString()} LBP
        </p>
      </footer>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PriceCheckerPage;
